import React, { useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { ACTION_REJECTED, baseScanUrl, mapToErrorMessage } from '@components/Web3/utils'
import { Button, FancyButton, IconProps, Stack, Text } from '@ui'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useChannelIdFromPathname } from 'hooks/useChannelIdFromPathname'
import { useEnvironment } from 'hooks/useEnvironment'
import { useSpaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { CHANNEL_INFO_PARAMS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { shortAddress } from 'ui/utils/utils'
import {
    fetchGuardianStatus,
    invalidateGuardianStatus,
    invalidateSpaceInfoQuery,
    invalidateSpaceOwner,
    SpaceInfo,
    useBaseReads,
    useDisableGuardian,
    useEnableGuardian,
    useTransferOwnership,
} from 'use-towns-client'
import { isAddress, toHex, zeroAddress } from 'viem'

import { TransferSchema } from '../transferAssetsSchema'

import { useOwnershipTransferStore } from './transferOwnership.store'
import { TransferState } from './types'

export function SubmitButton({
    tokenId,
    tokenName,
    tokenImgSrc,
    transferState,
    defaultCooldownTime,
    spaceInfo,
}: {
    tokenName: string | undefined
    tokenImgSrc: string | undefined
    tokenId: string | undefined
    transferState: TransferState
    defaultCooldownTime: string
    spaceInfo: SpaceInfo | undefined
}) {
    const [showGuardianModal, setShowGuardianModal] = useState<boolean>(false)
    const { submitOp, isLoading } = useSubmit({
        transferState,
        tokenId,
        defaultCooldownTime,
        spaceInfo,
        tokenName,
        tokenImgSrc,
        setShowGuardianModal,
    })

    const { handleSubmit, watch } = useFormContext<TransferSchema>()
    const recipient = watch('recipient')
    const onSubmit = (getSigner: GetSigner) => handleSubmit((data) => submitOp(data, getSigner))()
    const isDisabled =
        isLoading || (transferState === 'ready' && (!recipient || recipient === zeroAddress))

    if (!transferState) {
        return null
    }

    const label =
        transferState === 'none'
            ? 'Initiate Transfer'
            : transferState === 'cooldown-initiated'
              ? 'Cancel Transfer'
              : 'Transfer Ownership'

    return (
        <WalletReady>
            {({ getSigner }) => (
                <>
                    <Stack grow />
                    {transferState && (
                        <FancyButton
                            cta={!isLoading && transferState !== 'cooldown-initiated'}
                            color={transferState === 'cooldown-initiated' ? 'cta1' : undefined}
                            borderRadius="lg"
                            data-testid="submit-button"
                            disabled={isDisabled}
                            spinner={isLoading}
                            onClick={() => onSubmit(getSigner)}
                        >
                            {label}
                        </FancyButton>
                    )}
                    {!!showGuardianModal && (
                        <EnableGuardianModal onCancel={() => setShowGuardianModal(false)} />
                    )}
                </>
            )}
        </WalletReady>
    )
}

function useSubmit(args: {
    transferState: TransferState
    tokenId: string | undefined
    defaultCooldownTime: string
    spaceInfo: SpaceInfo | undefined
    tokenName: string | undefined
    tokenImgSrc: string | undefined
    setShowGuardianModal: (show: boolean) => void
}) {
    const {
        transferState,
        tokenId,
        defaultCooldownTime,
        spaceInfo,
        tokenName,
        tokenImgSrc,
        setShowGuardianModal,
    } = args
    const { data: myAbstractWalletAddress } = useMyAbstractAccountAddress()
    const { openPanel } = usePanelActions()
    const chainId = useEnvironment().baseChain.id
    const spaceId = useSpaceIdFromPathname()
    const channelId = useChannelIdFromPathname()

    const setinitiatedSpace = useOwnershipTransferStore((state) => state.setinitiatedSpace)
    const resetInitiatedSpace = useOwnershipTransferStore((state) => state.resetInitiatedSpace)

    const { spaceDapp } = useBaseReads()

    const { execute: disableGuardian, isLoading: isDisableGuardianLoading } = useDisableGuardian({
        onError: (error) => {
            popupFailedToast({
                error: error,
                source: 'disableGuardian',
                message: 'Failed to initiate transfer',
            })
        },
        onSuccess: async () => {
            if (myAbstractWalletAddress) {
                await invalidateGuardianStatus({ walletAddress: myAbstractWalletAddress })
                try {
                    const [, cooldownTime] = await fetchGuardianStatus({
                        spaceDapp: spaceDapp,
                        walletAddress: myAbstractWalletAddress,
                    })
                    if (cooldownTime) {
                        setinitiatedSpace({
                            imgSrc: tokenImgSrc,
                            name: tokenName,
                            spaceId,
                            channelId,
                            tokenId,
                            cooldownEnd: toHex(cooldownTime),
                        })
                    }
                } catch (error) {
                    console.error('Failed to fetch guardian status', error)
                }
            }
            popupSuccessToast({
                message: `Ownership transfer has been initiated. After a ${defaultCooldownTime} waiting period, you may complete it.`,
                iconProps: { type: 'time', color: 'gray2' },
            })
        },
    })

    const { execute: transferOwnership, isLoading: isTransferOwnershipLoading } =
        useTransferOwnership({
            onError: (error) => {
                popupFailedToast({
                    error,
                    source: 'transferOwnership',
                    message: 'Failed to transfer ownership',
                })
            },
            onSuccess: async ({ from, to, tokenId }, { transaction }) => {
                if (myAbstractWalletAddress && spaceId) {
                    await Promise.all([
                        invalidateGuardianStatus({ walletAddress: myAbstractWalletAddress }),
                        invalidateSpaceOwner(spaceId),
                        invalidateSpaceInfoQuery(spaceId),
                    ])
                }
                resetInitiatedSpace()
                popupSuccessToast({
                    message: `${spaceInfo?.name} was successfully sent to ${shortAddress(to)}.`,
                    cta: 'View Transaction',
                    ctaClick: async () => {
                        if (transaction && 'getUserOperationReceipt' in transaction) {
                            const hash = transaction.userOpHash
                            if (hash) {
                                window.open(
                                    `${baseScanUrl(chainId)}/tx/${hash}`,
                                    '_blank',
                                    'noopener,noreferrer',
                                )
                            }
                        }
                    },
                })
                openPanel(CHANNEL_INFO_PARAMS.TOWN_INFO)
            },
        })

    const submitOp = useCallback(
        async (data: TransferSchema, getSigner: GetSigner) => {
            const signer = await getSigner()
            if (!signer) {
                return
            }
            if (transferState === 'none') {
                await disableGuardian({ signer })
            } else if (transferState === 'cooldown-initiated') {
                setShowGuardianModal(true)
            } else if (transferState === 'ready' && myAbstractWalletAddress && tokenId) {
                if (
                    !data.recipient ||
                    data.recipient === zeroAddress ||
                    !isAddress(data.recipient)
                ) {
                    console.error(
                        '[TransferOwnershipPanel] recipient is required for transfer ',
                        data,
                    )
                    popupFailedToast({
                        error: new Error('Recipient is required'),
                        source: 'transferOwnership',
                        message: 'Recipient is required',
                    })
                    return
                }
                await transferOwnership({
                    from: myAbstractWalletAddress,
                    to: data.recipient,
                    tokenId,
                    signer,
                })
            }
        },
        [
            transferState,
            myAbstractWalletAddress,
            tokenId,
            disableGuardian,
            setShowGuardianModal,
            transferOwnership,
        ],
    )

    return {
        submitOp,
        isLoading: isDisableGuardianLoading || isTransferOwnershipLoading,
    }
}

function EnableGuardianModal(props: { onCancel: () => void }) {
    const { onCancel } = props
    const { data: myAbstractWalletAddress } = useMyAbstractAccountAddress()
    const resetInitiatedSpace = useOwnershipTransferStore((state) => state.resetInitiatedSpace)

    const { execute: enableGuardian, isLoading: isEnableGuardianLoading } = useEnableGuardian({
        onError: (error) => {
            popupFailedToast({
                error: error,
                source: 'enableGuardian',
                message: 'There was an error terminating the transfer.',
            })
        },
        onSuccess: async () => {
            onCancel()
            if (myAbstractWalletAddress) {
                await invalidateGuardianStatus({ walletAddress: myAbstractWalletAddress })
            }
            resetInitiatedSpace()
            popupSuccessToast({
                message: 'Termination succeeded.',
            })
        },
    })

    return (
        <ModalContainer minWidth="auto" onHide={onCancel}>
            <Stack padding="sm" gap="lg" alignItems="start" maxWidth="350">
                <Text fontWeight="strong">Are you sure you want to terminate this transfer?</Text>
                <Text>
                    You will have to restart your waiting period if you decide to terminate.
                </Text>
                <Stack horizontal gap width="100%">
                    <Stack grow />
                    <Button
                        tone="level2"
                        disabled={isEnableGuardianLoading}
                        rounded="lg"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <WalletReady>
                        {({ getSigner }) => (
                            <Button
                                tone="negative"
                                disabled={isEnableGuardianLoading}
                                rounded="lg"
                                color="default"
                                onClick={async () => {
                                    const signer = await getSigner()
                                    if (signer) {
                                        await enableGuardian({ signer })
                                    }
                                }}
                            >
                                {isEnableGuardianLoading && <ButtonSpinner />}
                                Terminate
                            </Button>
                        )}
                    </WalletReady>
                </Stack>
            </Stack>
        </ModalContainer>
    )
}

const popupFailedToast = (args: { error: Error | undefined; source: string; message: string }) => {
    const { error, source, message } = args
    const mappedError = mapToErrorMessage({ error, source })
    if (mappedError !== ACTION_REJECTED) {
        return popupToast(({ toast }) => (
            <StandardToast.Error toast={toast} message={message} subMessage={mappedError} />
        ))
    }
}

const popupSuccessToast = (args: {
    message: string
    cta?: string
    ctaClick?: () => void
    iconProps?: IconProps
}) => {
    const { message, iconProps, cta, ctaClick } = args
    return popupToast(({ toast }) => (
        <StandardToast.Success
            toast={toast}
            message={message}
            cta={cta}
            onCtaClick={ctaClick}
            {...(iconProps && { ...iconProps })}
        />
    ))
}
